import React, { useEffect } from 'react'
import { Button, H1, Lead, Message, Paragraph } from '@sbab-ui/components'
import { AppAction } from '../../appState.ts'
import BackArrow from '../../icons/BackArrow.tsx'
import { camelToKebab, snakeToKebab } from '../../lib/utils.ts'
import AppHeader from '../AppHeader.tsx'
import OneImg from '../../assets/steg1.jpg'
import TwoImg from '../../assets/steg2.jpg'
import ThreeImg from '../../assets/steg3.jpg'
import FourImg from '../../assets/steg4.jpg'
import FiveImg from '../../assets/steg5.jpg'

interface Props {
  children: React.ReactNode
  id: string
  appDispatch: React.Dispatch<AppAction>
  step?: number
  label: string
  description?: string
  errorMessage?: string
  inputRef: React.RefObject<HTMLInputElement | HTMLDivElement>
  submitButtonRef: React.RefObject<HTMLButtonElement>
  submitHandler: (event: React.MouseEvent<HTMLButtonElement>) => void
  disabled?: boolean
  loading?: boolean
}

export default function FormWrapper({
  children,
  id,
  appDispatch,
  step,
  label,
  description,
  errorMessage,
  inputRef,
  submitButtonRef,
  submitHandler,
  disabled,
  loading,
}: Props) {
  useEffect(() => {
    if (inputRef.current && window.innerWidth <= 768) {
      inputRef.current.blur()
    }
  }, [inputRef])

  const renderImage = () => {
    switch (step) {
      case 1:
        return (
          <img src={OneImg} alt="Gammal elcentral med sladdar" className={`h-[200px] w-full object-cover md:size-full md:h-screen`} />
        )
      case 2:
        return (
          <img src={TwoImg} alt="Två eluttag med kontakter i" className="h-[200px] w-full object-cover md:size-full md:h-screen" />
        )
      case 3:
        return (
          <img src={ThreeImg} alt="Vit lampa på fönsterbräda, kopplad till eluttag" className="h-[200px] w-full object-cover md:size-full md:h-screen" />
        )
      case 4:
        return (
          <img
            src={FourImg}
            alt="Elbilsladdare på gul vägg"
            className="h-[200px] w-full object-cover md:size-full  md:h-screen md:pl-10"
          />
        )
      case 5:
        return (
          <img src={FiveImg} alt="Rör i bronsfärgad metall, i olika storlekar" className="h-[200px] w-full object-cover md:size-full md:h-screen" />
        )
      default:
        return null
    }
  }

  const isLeftImage = step === 1 || step === 2 || step === 5

  return (
    <div data-testid={`${snakeToKebab(id)}-content`}>
      <AppHeader />
      <div className={`grow md:flex`}>
        <div className={`${isLeftImage ? '' : 'order-last'}`}>{renderImage()}</div>
        <div
          className={`ml-1 mr-2 flex grow-0 flex-col gap-12 p-1 md:ml-5 md:w-1/2 lg:ml-20 ${
            isLeftImage ? '' : 'md:order-none'
          }
          ${step === 5 ? 'lg:ml-24' : ''}
          `}
        >
          <button
            aria-label="Knapp tillbaka"
            className="mt-4 flex items-center md:mt-9"
            onClick={() => appDispatch({ type: 'goBack' })}
          >
            <BackArrow />
            <Lead className="!m-0" aria-label="Knapp tillbaka">
              Tillbaka
            </Lead>
          </button>
          <div
            className={`flex flex-col items-start gap-6 ${step === 5 ? '-mt-5 ' : '-mt-5 md:mt-48'} `}
          >
            <Lead className="!m-0 text-neutral-40">Steg {step}/5</Lead>
            <H1 className="!-mt-4 !text-2xl !text-black md:!text-[2rem]" id={"label-:r4:"}>{label}</H1>
            {description && (
              <Paragraph className="!-mt-3 text-neutral-40 lg:w-[610px]" id={"label-:r1:"}>{description}</Paragraph>
            )}
            {children}
            {errorMessage ? (
              <Message tone="critical" data-cy={`${camelToKebab(id)}-error-msg`}>
                {errorMessage}
              </Message>
            ) : null}
          </div>
          <div className="!-mt-5">
            <Button
              aria-label="Nästa"
              variant="primary"
              className={`!mb-20 !ml-0.5 !min-h-14 !w-full md:!w-44
                 ${step === 5 ? 'lg:!-mt-10 lg:!w-[220px]' : '!w-[175px]'} !text-xl`}
              ref={submitButtonRef}
              onClick={submitHandler}
              disabled={disabled}
              loading={loading}
            >
              {step === 5 ? 'Se resultat' : 'Nästa'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
